import { createMngmGroup } from 'account/components/ssoManagement/groups/new-actions'
import { listUsers } from 'account/components/userManagement/users/new-actions'
import { usersSelector } from 'account/components/userManagement/users/selectors'
import Progress from 'core/components/progress/Progress'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import useTenantSelector from '../tenants/useTenantSelector'
import TenantRolesTableFieldDefault from '../users/TenantRolesTableField'
import DomainPickList from '../domains/DomainPickList'
import { isSystemAdminUser } from 'core/utils/helpers'
import DomainNameTextField from '../domains/DomainNameTextField'

const TenantRolesTableField: any = TenantRolesTableFieldDefault // types on forward ref .js file dont work well.

interface Props {
  addRoute: Route
  domainId: string
}

const columns = [{ id: 'username', label: 'Name' }]

export default function CreateUserGroupModal({ addRoute, domainId }: Props) {
  const { history } = useReactRouter()
  const defaultParams = {
    name: '',
    description: '',
    users: [],
    roleAssignments: {},
    domainId: domainId,
  }
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { loading: loadingTenants, data: tenants } = useTenantSelector(params.domainId)

  const { loading: loadingUsers, reload: reloadUsers } = useListAction(listUsers, {
    params: {
      domainId: params.domainId,
    },
  })
  const users = useSelector(usersSelector)

  const { update, updating, error, reset } = useUpdateAction(createMngmGroup)

  useEffect(() => {
    reloadUsers(true)
  }, [params?.domainId])

  useEffect(() => {
    setParams({ ...defaultParams, domainId })
  }, [domainId])

  const submitForm = useCallback(async () => {
    const body = {
      name: params.name,
      description: params.description,
      roleAssignments: params.roleAssignments,
      users: params.users,
      domain_id: params.domainId,
    }
    const { success } = await update(body)
    if (success) {
      handleClose()
    }
  }, [params])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    history.push(routeManager.routes.userManagement.groups.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create User Group`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={error}
      initialValues={defaultParams}
      submitTitle={`Create User Group`}
    >
      <>
        <FormFieldSection title="User Group Settings">
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            required
          />
          <TextField
            id="description"
            label="Description"
            onChange={getParamsUpdater('description')}
            value={params.description}
          />
          {isSystemAdminUser() ? (
            <DomainPickList
              onChange={getParamsUpdater('domainId')}
              value={params.domainId}
              required
            />
          ) : (
            <DomainNameTextField domainId={params.domainId} isReadOnly />
          )}
        </FormFieldSection>
        <FormFieldSection title="Tenants & Roles">
          <Text variant="body2">
            Specify what Tenants this Group should have access to, and what role the users will be
            assigned in each Tenant.
          </Text>
          <Progress loading={loadingTenants} message={'Loading Tenants...'}>
            <TenantRolesTableField
              // validations={tenantRolesValidations}
              id="roleAssignments"
              tenants={tenants}
              onChange={getParamsUpdater('roleAssignments')}
              value={params.roleAssignments}
            />
          </Progress>
        </FormFieldSection>
        <FormFieldSection title="Users">
          <Text variant="body2">Specify which Users belong to this Group.</Text>
          <ListTableField
            id="users"
            data={users}
            loading={loadingUsers}
            columns={columns}
            onChange={getParamsUpdater('users')}
            value={params.users}
            uniqueIdentifier="id"
            searchTargets={['username']}
            multiSelection
          />
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
